import { Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect } from 'react';
import { LocalStorageService } from 'services';
import { LabelInfo } from 'shared/ui';
import { StyledFormMainWrapper } from 'components/styledComponents/form/Form.styled';
import { useFullscreen } from 'hooks';
import { autoRefreshPeriods } from 'constants/mapping';
import { StyledAutoRefresh } from 'components/styledComponents/select/Select.styled';
import { normalizeEnumLikeMap } from 'utils/helpers';
import AdminSelect from './AdminSelect';

interface AutoRefreshProps {
  tableKey: string;
  handleRefresh: (interval: number|undefined) => void;
  autoRefresh?: Record<string, number|undefined>;
  infoKey?: string;
}

const autoRefreshPeriodsObj = normalizeEnumLikeMap(autoRefreshPeriods);

const AutoRefresh = ({
  tableKey, handleRefresh, autoRefresh, infoKey,
}: AutoRefreshProps) => {
  const { fullscreen } = useFullscreen();

  const { t } = useTranslation();

  const [form] = Form.useForm();

  const { setFieldsValue } = form;

  const periodOptions = Object.entries(autoRefreshPeriodsObj).map(([el, value]) => ({
    id: +el,
    name: t(`autoRefreshPeriods.${value}`),
  }));

  const handleInterval = useCallback((interval: number) => setInterval(() => handleRefresh(interval), interval), [handleRefresh]);

  useEffect(() => {
    if (autoRefresh?.[tableKey]) {
      const interval = handleInterval(autoRefresh?.[tableKey]);
      return () => clearInterval(interval);
    }
  }, [handleInterval, autoRefresh, tableKey]);

  const handleChange = (val:string | number | undefined) => {
    const numericValue = val ? Number(val) : undefined;
    LocalStorageService.set('autoRefresh', { ...autoRefresh, [tableKey]: numericValue });
    handleRefresh(numericValue);
  };

  useEffect(() => {
    setFieldsValue(autoRefresh);
  }, [setFieldsValue, autoRefresh]);

  return (
    <StyledFormMainWrapper form={form}>
      <StyledAutoRefresh $fullscreen={fullscreen}>
        <Form.Item label={!fullscreen && (infoKey ? <LabelInfo label={`${t('autoRefresh')}:`} info={t(infoKey)} /> : t('autoRefresh'))} name={tableKey}>
          <AdminSelect
            placeholder={t(fullscreen ? 'autoRefresh' : 'select')}
            onChange={handleChange}
            data={periodOptions}
            allowClear
            isToString={false}
            size="large"
          />
        </Form.Item>
      </StyledAutoRefresh>
    </StyledFormMainWrapper>
  );
};

export default AutoRefresh;
